import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  AlertTitle,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { PageHint } from '@components/common/PageHint';
import OpacityIcon from '@mui/icons-material/Opacity';
import InfoIcon from '@mui/icons-material/Info';
import { getPmClientDailyNorms, getPmClientExpertNorms } from '@services/settings.service';
import { ExpertNormsTable } from '@components/clients/daily-norms/ExpertNormsTable';
import { DailyNormsResponse, UsedData } from '@/types/settings.types';
import { MetricInfoModal } from '@components/CalculatedMetrics/MetricInfoModal';

// Денні норми клієнта (не плутати з PmClientGoalsTab - цілі користувача).
interface PmClientDailyNormsTabProps {
  clientId: number;
}

const renderUsedData = (usedData: UsedData | undefined) => {
  if (!usedData) return <Typography variant="body2">Дані відсутні</Typography>;

  const dataToRender = {
    ...usedData.atomic,
    ...usedData.calculated,
    goal: usedData.goal,
    dietScheme: usedData.dietScheme,
  };

  return (
    <Box>
      {Object.entries(dataToRender).map(([key, value]) => {
        if (!value) return null;
        return (
          <Typography key={key} variant="body2">
            {key.charAt(0).toUpperCase() + key.slice(1)}: <b>{String(value)}</b>
          </Typography>
        );
      })}
    </Box>
  );
};

export const PmClientDailyNormsTab: React.FC<PmClientDailyNormsTabProps> = ({ clientId }) => {
  const [goals, setGoals] = useState<DailyNormsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedMetricForInfo, setSelectedMetricForInfo] = useState<string | null>(null);

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPmClientDailyNorms(clientId);
        setGoals(data);
      } catch (err) {
        setError('Не вдалося завантажити денні норми клієнта.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [clientId]);

  const handleOpenInfoModal = (metric: string) => {
    setSelectedMetricForInfo(metric);
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    setSelectedMetricForInfo(null);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!goals || !goals.data) {
    return <Typography>Немає даних для відображення.</Typography>;
  }

  const { data } = goals;
  const calculationDetails = data.calculationDetails;

  const proteinG = Number(data.proteinG ?? 0);
  const fatG = Number(data.fatG ?? 0);
  const carbsG = Number(data.carbsG ?? 0);

  const proteinCalories = proteinG * 4;
  const fatCalories = fatG * 9;
  const carbsCalories = carbsG * 4;
  const totalNutrientCalories = proteinCalories + fatCalories + carbsCalories;

  const proteinPercentage = totalNutrientCalories > 0 ? (proteinCalories / totalNutrientCalories) * 100 : 0;
  const fatPercentage = totalNutrientCalories > 0 ? (fatCalories / totalNutrientCalories) * 100 : 0;
  const carbsPercentage = totalNutrientCalories > 0 ? (carbsCalories / totalNutrientCalories) * 100 : 0;

  return (
    <Box data-i-doc={PageIndices.PM_CLIENT_DAILY_NORMS}>
      <PageHint iDoc={PageIndices.PM_CLIENT_DAILY_NORMS} />
        <Typography variant="h6" gutterBottom>Денні норми клієнта</Typography>
        <Alert severity="info" sx={{ my: 2 }}>
          <AlertTitle>Розраховані денні норми клієнта</AlertTitle>
          Ці розрахунки базуються на внутрішніх алгоритмах системи, включаючи ШІ, на основі даних, наданих клієнтом.
        </Alert>

        {/* Water Block */}
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom component="div">Вода</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OpacityIcon sx={{ color: '#1976d2', mr: 1, fontSize: '2.5rem' }} />
                <Typography variant="h4" sx={{ color: '#1976d2' }}>{data.waterMl ?? '—'} мл</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '46px' }}>Розраховане значення</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.water?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.water?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Calories Block */}
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom component="div">Калорії</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BoltIcon sx={{ color: '#2e7d32', mr: 1, fontSize: '2.5rem' }} />
                <Typography variant="h4" sx={{ color: '#2e7d32' }}>{data.caloriesIntake ?? '—'} ккал</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '46px' }}>Цільове споживання</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <BoltIcon sx={{ color: '#d32f2f', mr: 1, fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ color: '#d32f2f' }}>{data.caloriesBurned ?? '—'} ккал</Typography>
              </Box>
              <Typography variant="caption" sx={{ ml: '30px' }}>Витрата на активність</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.calories?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.calories?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Nutrients Block */}
        <Paper sx={{ p: 3, my: 2 }}>
          <Typography variant="h5" gutterBottom>Нутрієнти (БЖВ)</Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="h5" component="span" sx={{ color: '#7CB342' }}>{data.proteinG == null ? '—' : `${data.proteinG} г (${proteinPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Білки</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <Typography variant="h5" component="span" sx={{ color: '#FB8C00' }}>{data.fatG == null ? '—' : `${data.fatG} г (${fatPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Жири</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography variant="h5" component="span" sx={{ color: '#D32F2F' }}>{data.carbsG == null ? '—' : `${data.carbsG} г (${carbsPercentage.toFixed(0)}%)`}</Typography>
                <Typography variant="caption" sx={{ ml: 1 }}>Вуглеводи</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані дані для розрахунків</Typography>
              {renderUsedData(calculationDetails?.nutrients?.usedData)}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>використані калькулятори</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {calculationDetails?.nutrients?.calculators?.map(calc => (
                  <Chip 
                    key={calc.name} 
                    label={calc.name} 
                    onDelete={calc.infoLink ? () => handleOpenInfoModal(calc.infoLink!) : undefined}
                    deleteIcon={calc.infoLink ? <InfoIcon /> : undefined}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mt: 3 }}>
          <ExpertNormsTable clientId={clientId} load={getPmClientExpertNorms} />
        </Paper>

        <MetricInfoModal
          open={isInfoModalOpen}
          onClose={handleCloseInfoModal}
          metricName={selectedMetricForInfo || ''}
        />
    </Box>
  );
};
